/* eslint linebreak-style: ["error", "windows"] */

/* -------------------------------------------------------------------------- *
 * Responder *
 * -------------------------------------------------------------------------- *
 * Responder updates and stores values for window height, width, and scroll.
 * Modules can access these values through the Events module or directly from
 * the Responder module (e.g. Responder.height).
 */

import Events from './Events';

/* --------------- *
 * Window Resizing *
 * --------------- *
 * Tracks and updates changes to window height and width. The updates are
 * published via the Events module using the name 'resize'. The event publishes
 * a single object with values for both width and height. For performance,
 * updates to the dimensions are debounced so you will notice a slight delay.
 * This function also updates a custom css property '--100vh' that can be used
 * to get the true 100vh on mobile devices.
 */

const doc_style = document.documentElement.style;
let debounce;

function updateDimensions() {
    clearTimeout(debounce);

    debounce = setTimeout(() => {
        // Get current dimensions
        const dimensions = {
            height: window.innerHeight,
            width: window.innerWidth,
        };

        // Publish dimensions
        Events.publish('resize', dimensions);

        // Update custom CSS property
        doc_style.setProperty('--100vh', `${dimensions.height}px`);
    }, 250);
}

/* --------------- *
 * Scroll Position *
 * --------------- *
 * Tracks and updates the scroll position using the document scrollTop. The
 * updates are published via the Events module using the name 'scroll'. For
 * performance, updates to the scroll position are throttled using
 * requestAnimationFrame.
 */

const scroll_el = document.scrollingElement || document.documentElement;
let ticking = false;

function updateScroll() {
    function requestTick() {
        if (ticking === false) {
            window.requestAnimationFrame(() => {
                // Publish dimensions
                Events.publish('scroll', scroll_el.scrollTop);

                ticking = false;
            });
        }

        ticking = true;
    }

    // Throttle updates
    requestTick();
}

/* ------------- *
 * Module Export *
 * ------------- */

const Responder = {
    // Set initial screen dimensions
    width: window.innerWidth,
    height: window.innerHeight,

    // Set initial scroll position
    scroll_top: 0,

    init() {
        // Keep the public window dimensions updated
        Events.subscribe('resize', (dimensions) => {
            Responder.width = dimensions.width;
            Responder.height = dimensions.height;
        });

        // Set the initial value for the custom CSS property
        doc_style.setProperty('--100vh', `${Responder.height}px`);

        // Keep the public scroll position updated
        Events.subscribe('scroll', (scrolled) => {
            Responder.scroll_top = scrolled;
        });

        // Add event listeners
        window.addEventListener('resize', updateDimensions);
        window.addEventListener('scroll', updateScroll);
    },
};

export default Responder;
